import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FC } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";

import AnimatedModal from "components/layout/motion/AnimatedModal";
import FunctionalButton from "components/layout/FunctionalButton";
import { ReactComponent as CloseIcon } from "assets/svgs/Close.svg";
import { ReactComponent as PlayIcon } from "assets/svgs/navbar/play.svg";
import { ReactComponent as PauseIcon } from "assets/svgs/navbar/pause.svg";
import { ReactComponent as BinIcon } from "assets/svgs/Editor/bin.svg";
import {
    deleteAudioTrack,
    listAudioTracks,
    previewAudioTrack,
    stopPreviewAudio,
    uploadAudioTrack,
    type AudioTrack,
} from "lib/audioTracksApi";
import { toUserFacingApiError } from "lib/utils/userFacingApiError";
import { useToast } from "providers/ToastProvider";
import type { RootState } from "store";

interface PatrolModuleSettingsDialogProps {
    visible: boolean;
    onClose: () => void;
}

const formatDuration = (durationMs: number) => {
    const totalSec = Math.max(0, Math.round(durationMs / 1000));
    const minutes = Math.floor(totalSec / 60);
    const seconds = totalSec % 60;
    return `${minutes}:${String(seconds).padStart(2, "0")}`;
};

const PatrolModuleSettingsDialog: FC<PatrolModuleSettingsDialogProps> = ({
    visible,
    onClose,
}) => {
    const { t } = useTranslation(["settings", "common"]);
    const { addToast } = useToast();
    const { arcId, arcProxyHost } = useSelector((state: RootState) => state.arcProxy);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const previewTimerRef = useRef<number | null>(null);

    const [tracks, setTracks] = useState<AudioTrack[]>([]);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [deletingId, setDeletingId] = useState<string | null>(null);
    const [playingTrackId, setPlayingTrackId] = useState<string | null>(null);
    const [previewBusy, setPreviewBusy] = useState(false);

    const clearPreviewTimer = useCallback(() => {
        if (previewTimerRef.current != null) {
            window.clearTimeout(previewTimerRef.current);
            previewTimerRef.current = null;
        }
    }, []);

    const refreshTracks = useCallback(async () => {
        setLoading(true);
        try {
            const next = await listAudioTracks(arcId, arcProxyHost);
            setTracks(next);
        } catch (error) {
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    error,
                    t("payload.patrol.audioLoadFailed"),
                ),
            });
        } finally {
            setLoading(false);
        }
    }, [addToast, arcId, arcProxyHost, t]);

    const stopPreview = useCallback(async () => {
        clearPreviewTimer();
        setPreviewBusy(true);
        try {
            await stopPreviewAudio(arcId, arcProxyHost);
        } catch (error) {
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    error,
                    t("payload.patrol.audioStopFailed"),
                ),
            });
        } finally {
            setPlayingTrackId(null);
            setPreviewBusy(false);
        }
    }, [addToast, arcId, arcProxyHost, clearPreviewTimer, t]);

    useEffect(() => {
        if (!visible) return;
        void refreshTracks();
    }, [visible, refreshTracks]);

    useEffect(() => {
        if (visible) return;
        clearPreviewTimer();
        setPlayingTrackId((current) => {
            if (current) {
                void stopPreviewAudio(arcId, arcProxyHost).catch(() => undefined);
            }
            return null;
        });
    }, [visible, arcId, arcProxyHost, clearPreviewTimer]);

    const handleUpload = async (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        event.target.value = "";
        if (!file) return;
        setUploading(true);
        try {
            const track = await uploadAudioTrack(file, undefined, arcId, arcProxyHost);
            setTracks((prev) => [track, ...prev.filter((item) => item.id !== track.id)]);
            addToast({
                type: "success",
                message: t("payload.patrol.audioUploaded", { name: track.name }),
            });
        } catch (error) {
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    error,
                    t("payload.patrol.audioUploadFailed"),
                ),
            });
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (trackId: string) => {
        if (playingTrackId === trackId) {
            await stopPreview();
        }
        setDeletingId(trackId);
        try {
            await deleteAudioTrack(trackId, arcId, arcProxyHost);
            setTracks((prev) => prev.filter((track) => track.id !== trackId));
            addToast({
                type: "success",
                message: t("payload.patrol.audioDeleted"),
            });
        } catch (error) {
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    error,
                    t("payload.patrol.audioDeleteFailed"),
                ),
            });
        } finally {
            setDeletingId(null);
        }
    };

    const handleTogglePreview = async (track: AudioTrack) => {
        if (previewBusy) return;
        if (playingTrackId === track.id) {
            await stopPreview();
            return;
        }
        clearPreviewTimer();
        setPreviewBusy(true);
        try {
            if (playingTrackId) {
                await stopPreviewAudio(arcId, arcProxyHost);
            }
            await previewAudioTrack(track.id, 80, arcId, arcProxyHost);
            setPlayingTrackId(track.id);
            const durationMs = Math.max(500, Number(track.duration_ms) || 0) + 250;
            previewTimerRef.current = window.setTimeout(() => {
                setPlayingTrackId((current) => (current === track.id ? null : current));
                previewTimerRef.current = null;
            }, durationMs);
        } catch (error) {
            setPlayingTrackId(null);
            addToast({
                type: "error",
                message: toUserFacingApiError(
                    error,
                    t("payload.patrol.audioPlayFailed"),
                ),
            });
        } finally {
            setPreviewBusy(false);
        }
    };

    const handleClose = () => {
        if (playingTrackId) {
            void stopPreview();
        }
        onClose();
    };

    return (
        <AnimatedModal
            visible={visible}
            onClose={handleClose}
            className="relative w-full max-w-md rounded-r10 border border-black80 bg-black90 p-6"
        >
            <FunctionalButton
                icon={<CloseIcon />}
                className="absolute right-3 top-3"
                onClick={handleClose}
            />
            <h2 className="mb-2 pr-8 text-h4 font-semibold text-white">
                {t("payload.patrol.settingsTitle")}
            </h2>
            <p className="mb-6 text-h5 text-black40">
                {t("payload.patrol.settingsDescription")}
            </p>

            <div className="mb-4 flex flex-col gap-2">
                <input
                    ref={fileInputRef}
                    type="file"
                    accept=".wav,.mp3,audio/wav,audio/x-wav,audio/mpeg,audio/mp3"
                    className="hidden"
                    onChange={(e) => void handleUpload(e)}
                />
                <FunctionalButton
                    title={
                        uploading
                            ? t("payload.patrol.uploading")
                            : t("payload.patrol.uploadWav")
                    }
                    className="rounded-r5 border border-primary2 bg-primary2 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:bg-primary1 disabled:opacity-50"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                />
                <p className="text-h5 text-black60">{t("payload.patrol.uploadHint")}</p>
            </div>

            <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                    <p className="text-h5 font-semibold text-white">
                        {t("payload.patrol.tracksTitle")}
                    </p>
                    <FunctionalButton
                        title={t("common:refresh")}
                        className="rounded-r5 border border-black60 bg-black90 px-3 py-1 !normal-case text-h5 text-white hover:border-black40 disabled:opacity-50"
                        onClick={() => void refreshTracks()}
                        disabled={loading || uploading || previewBusy}
                    />
                </div>
                {loading ? (
                    <p className="text-h5 text-black40">{t("common:loading")}</p>
                ) : tracks.length === 0 ? (
                    <p className="text-h5 text-black40">{t("payload.patrol.noTracks")}</p>
                ) : (
                    <ul className="max-h-64 space-y-2 overflow-y-auto">
                        {tracks.map((track) => {
                            const isPlaying = playingTrackId === track.id;
                            return (
                                <li
                                    key={track.id}
                                    className="flex items-center justify-between gap-3 rounded-r5 border border-black70 bg-black100 px-3 py-2"
                                >
                                    <div className="min-w-0">
                                        <p className="truncate text-h5 font-semibold text-white">
                                            {track.name}
                                        </p>
                                        <p className="text-h5 text-black40">
                                            {formatDuration(track.duration_ms)} ·{" "}
                                            {Math.max(1, Math.round(track.size_bytes / 1024))} KB
                                        </p>
                                    </div>
                                    <div className="flex shrink-0 items-center gap-2">
                                        <button
                                            type="button"
                                            aria-label={
                                                isPlaying
                                                    ? t("payload.patrol.pause")
                                                    : t("payload.patrol.play")
                                            }
                                            title={
                                                isPlaying
                                                    ? t("payload.patrol.pause")
                                                    : t("payload.patrol.play")
                                            }
                                            className={`inline-flex h-8 w-8 items-center justify-center rounded-r5 border disabled:opacity-50 ${
                                                isPlaying
                                                    ? "border-primary2 text-primary2 hover:bg-primary2/10"
                                                    : "border-black60 text-white hover:border-black40"
                                            }`}
                                            disabled={previewBusy || deletingId === track.id}
                                            onClick={() => void handleTogglePreview(track)}
                                        >
                                            {isPlaying ? (
                                                <PauseIcon className="h-4 w-4" aria-hidden />
                                            ) : (
                                                <PlayIcon className="h-4 w-4" aria-hidden />
                                            )}
                                        </button>
                                        <button
                                            type="button"
                                            aria-label={t("common:delete")}
                                            title={
                                                deletingId === track.id
                                                    ? t("common:working")
                                                    : t("common:delete")
                                            }
                                            className="inline-flex h-8 w-8 items-center justify-center rounded-r5 border border-black60 text-warning hover:border-warning disabled:opacity-50"
                                            disabled={
                                                deletingId === track.id || previewBusy
                                            }
                                            onClick={() => void handleDelete(track.id)}
                                        >
                                            <BinIcon className="h-4 w-4" aria-hidden />
                                        </button>
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>

            <div className="mt-6 flex justify-end">
                <FunctionalButton
                    title={t("common:close")}
                    className="rounded-r5 border border-black60 bg-black90 px-4 py-2 !normal-case text-h5 font-semibold text-white hover:border-black40"
                    onClick={handleClose}
                />
            </div>
        </AnimatedModal>
    );
};

export default PatrolModuleSettingsDialog;
